import React, { useState, useEffect, useRef } from "react";
import Barcode from "react-barcode";
import { QRCodeSVG } from "qrcode.react";
import { useReactToPrint } from "react-to-print";
import api from "../api";

const BarcodePage = () => {
    const [services, setServices] = useState([]);
    const [search, setSearch] = useState("");
    const [selectedItems, setSelectedItems] = useState([]); // { ...service, printQty: 1 }

    // Settings
    const [settings, setSettings] = useState({
        paperSize: "a4", // a4
        cols: 3,
        rows: 11,
        labelWidth: 65, // mm
        labelHeight: 25, // mm
        gapX: 2, // mm
        gapY: 2, // mm
        fontSize: 12,
        barcodeWidth: 1.5,
        barcodeHeight: 40,
        startOffset: 0, // Skip first N labels
        showName: true,
        showPrice: true,
        showCode: true,
        barcodeType: "1D", // "1D" or "2D"
        alignment: "center", // "left", "center", "right"
        padding: 2, // mm
        autoFit: true, // New setting
        pageMargin: 5, // mm (Page padding)
    });

    // Preview Scaling
    const [previewScale, setPreviewScale] = useState(1);
    const previewContainerRef = useRef(null);
    const componentRef = useRef();

    // Auto Fit Logic
    useEffect(() => {
        if (settings.autoFit) {
            const a4Width = 210; // mm
            const a4Height = 297; // mm

            // Available Space = Page Size - (Margin * 2)
            const availWidth = a4Width - (settings.pageMargin * 2);
            const availHeight = a4Height - (settings.pageMargin * 2);

            // Calculate Width
            // Total Gap X = (cols - 1) * gapX
            // Available Width for Labels = Available Space - Total Gap
            const totalGapX = (settings.cols - 1) * settings.gapX;
            const newWidth = (availWidth - totalGapX) / settings.cols;

            // Calculate Height
            const totalGapY = (settings.rows - 1) * settings.gapY;
            const newHeight = (availHeight - totalGapY) / settings.rows;

            setSettings(prev => ({
                ...prev,
                labelWidth: Math.max(0, Number(newWidth.toFixed(2))),
                labelHeight: Math.max(0, Number(newHeight.toFixed(2)))
            }));
        }
    }, [settings.cols, settings.rows, settings.gapX, settings.gapY, settings.autoFit, settings.pageMargin]);

    // Helper for alignment styles
    const getAlignmentStyle = () => {
        const { alignment, barcodeType } = settings;
        if (barcodeType === "1D") {
            // Column layout
            switch (alignment) {
                case "left": return { alignItems: "flex-start", textAlign: "left", justifyContent: "flex-start" };
                case "right": return { alignItems: "flex-end", textAlign: "right", justifyContent: "flex-end" };
                default: return { alignItems: "center", textAlign: "center", justifyContent: "center" };
            }
        } else {
            // Row layout (2D)
            // For the main container (flex-direction: row)
            switch (alignment) {
                case "left": return { justifyContent: "flex-start", textAlign: "left" };
                case "right": return { justifyContent: "flex-end", textAlign: "right" };
                default: return { justifyContent: "center", textAlign: "left" }; // Text inside usually stays left in 2D row
            }
        }
    };

    const alignStyle = getAlignmentStyle();

    useEffect(() => {
        fetchServices();
    }, []);

    useEffect(() => {
        const handleResize = () => {
            if (previewContainerRef.current) {
                const container = previewContainerRef.current;
                const containerWidth = container.clientWidth;
                const containerHeight = container.clientHeight;

                // A4 dimensions in px (approx 96 DPI)
                // 210mm = 794px, 297mm = 1123px
                const a4Width = 794;
                const a4Height = 1123;

                // Calculate scale to fit
                // Subtract some padding (e.g. 40px)
                const scaleX = (containerWidth - 40) / a4Width;
                const scaleY = (containerHeight - 40) / a4Height;

                // Use the smaller scale to fit both width and height, max 1
                const scale = Math.min(scaleX, scaleY, 1);
                setPreviewScale(scale > 0 ? scale : 0.1);
            }
        };

        // Initial calc
        handleResize();

        // Listen for resize
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const fetchServices = async () => {
        try {
            const res = await api.get("/services");
            setServices(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    const handleAddItem = (service) => {
        setSelectedItems((prev) => {
            const existing = prev.find((i) => i.service_id === service.service_id);
            if (existing) {
                return prev.map((i) =>
                    i.service_id === service.service_id
                        ? { ...i, printQty: i.printQty + 1 }
                        : i
                );
            }
            return [...prev, { ...service, printQty: 1 }];
        });
    };

    const handleRemoveItem = (id) => {
        setSelectedItems((prev) => prev.filter((i) => i.service_id !== id));
    };

    const handleUpdateQty = (id, qty) => {
        if (qty < 1) return;
        setSelectedItems((prev) =>
            prev.map((i) => (i.service_id === id ? { ...i, printQty: qty } : i))
        );
    };

    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
        pageStyle: `
      @page {
        size: A4;
        margin: 0;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
        }
      }
    `,
    });

    // Presets
    const applyPreset = (type) => {
        if (type === "3x11") {
            setSettings((prev) => ({
                ...prev,
                cols: 3,
                rows: 11,
                labelWidth: 64,
                labelHeight: 25.4,
                gapX: 2,
                gapY: 0,
                fontSize: 12,
                barcodeHeight: 35,
                padding: 2,
                autoFit: true, // Force auto fit for presets
                pageMargin: 5,
            }));
        } else if (type === "3x8") {
            setSettings((prev) => ({
                ...prev,
                cols: 3,
                rows: 8,
                labelWidth: 70,
                labelHeight: 37,
                gapX: 0,
                gapY: 0,
                fontSize: 14,
                barcodeHeight: 50,
                padding: 2,
                autoFit: true,
                pageMargin: 5,
            }));
        } else if (type === "4x11") { // A4 44 ดวง
            setSettings((prev) => ({
                ...prev,
                cols: 4,
                rows: 11,
                labelWidth: 48.5,
                labelHeight: 25.4,
                gapX: 1,
                gapY: 0,
                fontSize: 10,
                barcodeHeight: 30,
                barcodeWidth: 1.2,
                padding: 1,
                autoFit: true,
                pageMargin: 5,
            }));
        }
    };

    // Generate Label List
    const generateLabels = () => {
        let labels = [];
        // Add offset (blank labels)
        for (let i = 0; i < settings.startOffset; i++) {
            labels.push(null);
        }
        // Add items
        selectedItems.forEach((item) => {
            for (let i = 0; i < item.printQty; i++) {
                labels.push(item);
            }
        });
        return labels;
    };

    const labels = generateLabels();

    return (
        <div className="barcode-page barcode-view-wrapper" style={{ background: "#f8f9fa", display: "flex", flexDirection: "column" }}>
            <div className="container-fluid py-3 flex-grow-1 barcode-view-body">
                <div className="row barcode-view-row">
                    {/* Left Panel: Product Selection */}
                    <div className="col-lg-3 no-print barcode-panel d-flex flex-column mb-3 mb-lg-0">
                        <div className="card shadow-sm h-100 d-flex flex-column">
                            <div className="card-header bg-white fw-bold">
                                <i className="bi bi-cart-plus me-2"></i>เลือกสินค้า
                            </div>
                            <div className="card-body d-flex flex-column overflow-hidden">
                                <input
                                    type="text"
                                    className="form-control mb-3"
                                    placeholder="ค้นหาชื่อ / บาร์โค้ด..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                                <div className="list-group mb-3 flex-grow-1" style={{ overflowY: "auto" }}>
                                    {services
                                        .filter(s => s.service_name.toLowerCase().includes(search.toLowerCase()) || (s.barcode || "").includes(search))
                                        .slice(0, 20)
                                        .map(s => (
                                            <button
                                                key={s.service_id}
                                                className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                                                onClick={() => handleAddItem(s)}
                                            >
                                                <div className="text-truncate" style={{ maxWidth: "150px" }}>
                                                    <small className="d-block fw-bold">{s.service_name}</small>
                                                    <small className="text-muted">{s.barcode || "No Barcode"}</small>
                                                </div>
                                                <span className="badge bg-light text-dark border">
                                                    {Math.floor(s.price)} ฿
                                                </span>
                                            </button>
                                        ))}
                                </div>

                                <hr />
                                <h6 className="small fw-bold">รายการที่เลือก ({selectedItems.length})</h6>
                                <div style={{ height: "30%", overflowY: "auto" }}>
                                    {selectedItems.map((item) => (
                                        <div key={item.service_id} className="d-flex align-items-center justify-content-between mb-2 p-2 border rounded bg-light">
                                            <div className="text-truncate" style={{ maxWidth: "100px" }}>
                                                <small className="fw-bold d-block">{item.service_name}</small>
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                                <input
                                                    type="number"
                                                    className="form-control form-control-sm text-center"
                                                    style={{ width: "50px" }}
                                                    value={item.printQty}
                                                    onChange={(e) => handleUpdateQty(item.service_id, Number(e.target.value))}
                                                />
                                                <button className="btn btn-sm btn-outline-danger" onClick={() => handleRemoveItem(item.service_id)}>
                                                    <i className="bi bi-trash"></i>
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Center Panel: Preview */}
                    <div className="col-lg-6 barcode-panel d-flex flex-column mb-3 mb-lg-0">
                        <div className="card shadow-sm h-100 bg-secondary bg-opacity-10 d-flex flex-column align-items-center p-2 overflow-hidden">
                            <div className="alert alert-warning small w-100 text-center mb-2 shadow-sm no-print py-1">
                                <i className="bi bi-exclamation-triangle me-2"></i>
                                ระบบรองรับ <strong>กระดาษสติ๊กเกอร์ A4</strong> เท่านั้น
                            </div>

                            <div
                                ref={previewContainerRef}
                                className="flex-grow-1 w-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-25 rounded overflow-hidden"
                            >
                                <div
                                    ref={componentRef}
                                    className="print-preview-content bg-white shadow-lg"
                                    style={{
                                        width: "210mm",
                                        height: "297mm", // Fixed height for A4
                                        padding: `${settings.pageMargin}mm`,
                                        display: "grid",
                                        gridTemplateColumns: `repeat(${settings.cols}, 1fr)`,
                                        gridAutoRows: `${settings.labelHeight}mm`,
                                        columnGap: `${settings.gapX}mm`,
                                        rowGap: `${settings.gapY}mm`,
                                        alignContent: "start",
                                        transform: `scale(${previewScale})`,
                                        transformOrigin: "center center",
                                    }}
                                >
                                    {labels.map((item, idx) => (
                                        <div
                                            key={idx}
                                            style={{
                                                width: `${settings.labelWidth}mm`,
                                                height: `${settings.labelHeight}mm`,
                                                border: "1px dashed #ddd", // Show border in preview only? Or keep light border
                                                display: "flex",
                                                flexDirection: settings.barcodeType === "2D" ? "row" : "column",
                                                alignItems: alignStyle.alignItems || "center",
                                                justifyContent: alignStyle.justifyContent || "center",
                                                overflow: "hidden",
                                                padding: `${settings.padding}mm`,
                                                visibility: item ? "visible" : "hidden", // Hide offset items
                                            }}
                                            className="barcode-label"
                                        >
                                            {item && (
                                                settings.barcodeType === "2D" ? (
                                                    // 2D Layout: QR Left, Text Right
                                                    <>
                                                        <div style={{ flexShrink: 0, marginRight: "4px", display: "flex", alignItems: "center" }}>
                                                            <QRCodeSVG
                                                                value={item.barcode || item.service_id.toString()}
                                                                size={settings.barcodeHeight} // Use height for square QR
                                                                level={"M"}
                                                            />
                                                        </div>
                                                        <div style={{
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            justifyContent: "center",
                                                            textAlign: "left",
                                                            overflow: "hidden"
                                                        }}>
                                                            {settings.showName && (
                                                                <div
                                                                    style={{
                                                                        fontSize: `${settings.fontSize}px`,
                                                                        fontWeight: "bold",
                                                                        lineHeight: "1.1",
                                                                        marginBottom: "2px",
                                                                        whiteSpace: "nowrap",
                                                                        overflow: "hidden",
                                                                        textOverflow: "ellipsis",
                                                                        maxWidth: `${settings.labelWidth - (settings.barcodeHeight / 3.78) - 5 - (settings.padding * 2)}mm` // Adjust max width for padding
                                                                    }}
                                                                >
                                                                    {item.service_name}
                                                                </div>
                                                            )}
                                                            {settings.showPrice && (
                                                                <div style={{ fontSize: `${settings.fontSize}px`, fontWeight: "bold" }}>
                                                                    {Math.floor(item.price)} ฿
                                                                </div>
                                                            )}
                                                            {settings.showCode && (
                                                                <div style={{ fontSize: `${Math.max(8, settings.fontSize - 2)}px`, color: "#666" }}>
                                                                    {item.barcode || item.service_id}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </>
                                                ) : (
                                                    // 1D Layout: Vertical Stack
                                                    <>
                                                        {settings.showName && (
                                                            <div
                                                                style={{
                                                                    fontSize: `${settings.fontSize}px`,
                                                                    fontWeight: "bold",
                                                                    textAlign: alignStyle.textAlign,
                                                                    lineHeight: "1.1",
                                                                    marginBottom: "2px",
                                                                    whiteSpace: "nowrap",
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                    width: "100%",
                                                                }}
                                                            >
                                                                {item.service_name}
                                                            </div>
                                                        )}
                                                        <div style={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: alignStyle.justifyContent || 'center', width: '100%', overflow: 'hidden' }}>
                                                            <Barcode
                                                                value={item.barcode || item.service_id.toString()}
                                                                width={settings.barcodeWidth}
                                                                height={settings.barcodeHeight}
                                                                fontSize={settings.showCode ? settings.fontSize : 0}
                                                                displayValue={settings.showCode}
                                                                margin={0}
                                                                renderer="svg"
                                                            />
                                                        </div>
                                                        {settings.showPrice && (
                                                            <div style={{ fontSize: `${settings.fontSize}px`, fontWeight: "bold", textAlign: alignStyle.textAlign, width: "100%" }}>
                                                                {Math.floor(item.price)} ฿
                                                            </div>
                                                        )}
                                                    </>
                                                )
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Panel: Settings */}
                    <div className="col-lg-3 no-print barcode-panel d-flex flex-column mb-3 mb-lg-0">
                        <div className="card shadow-sm h-100 d-flex flex-column">
                            <div className="card-header bg-white fw-bold">
                                <i className="bi bi-gear me-2"></i>ตั้งค่าการพิมพ์
                            </div>
                            <div className="card-body overflow-auto">
                                {/* Barcode Type Toggle */}
                                <div className="mb-3">
                                    <label className="form-label small fw-bold">รูปแบบโค้ด</label>
                                    <div className="btn-group w-100" role="group">
                                        <input
                                            type="radio"
                                            className="btn-check"
                                            name="barcodeType"
                                            id="type1D"
                                            autoComplete="off"
                                            checked={settings.barcodeType === "1D"}
                                            onChange={() => setSettings({ ...settings, barcodeType: "1D" })}
                                        />
                                        <label className="btn btn-outline-gold btn-sm" htmlFor="type1D">Barcode (1D)</label>

                                        <input
                                            type="radio"
                                            className="btn-check"
                                            name="barcodeType"
                                            id="type2D"
                                            autoComplete="off"
                                            checked={settings.barcodeType === "2D"}
                                            onChange={() => setSettings({ ...settings, barcodeType: "2D" })}
                                        />
                                        <label className="btn btn-outline-gold btn-sm" htmlFor="type2D">QR Code (2D)</label>
                                    </div>
                                </div>

                                <hr />

                                {/* Alignment */}
                                <div className="mb-3">
                                    <label className="form-label small fw-bold">การจัดวาง</label>
                                    <div className="btn-group w-100" role="group">
                                        <input
                                            type="radio"
                                            className="btn-check"
                                            name="alignment"
                                            id="alignLeft"
                                            autoComplete="off"
                                            checked={settings.alignment === "left"}
                                            onChange={() => setSettings({ ...settings, alignment: "left" })}
                                        />
                                        <label className="btn btn-outline-secondary btn-sm" htmlFor="alignLeft">
                                            <i className="bi bi-text-left"></i> ซ้าย
                                        </label>

                                        <input
                                            type="radio"
                                            className="btn-check"
                                            name="alignment"
                                            id="alignCenter"
                                            autoComplete="off"
                                            checked={settings.alignment === "center"}
                                            onChange={() => setSettings({ ...settings, alignment: "center" })}
                                        />
                                        <label className="btn btn-outline-secondary btn-sm" htmlFor="alignCenter">
                                            <i className="bi bi-text-center"></i> กลาง
                                        </label>

                                        <input
                                            type="radio"
                                            className="btn-check"
                                            name="alignment"
                                            id="alignRight"
                                            autoComplete="off"
                                            checked={settings.alignment === "right"}
                                            onChange={() => setSettings({ ...settings, alignment: "right" })}
                                        />
                                        <label className="btn btn-outline-secondary btn-sm" htmlFor="alignRight">
                                            <i className="bi bi-text-right"></i> ขวา
                                        </label>
                                    </div>
                                </div>

                                <hr />

                                {/* Presets */}
                                <div className="mb-3">
                                    <label className="form-label small fw-bold">Preset กระดาษ (A4)</label>
                                    <div className="d-flex gap-2 flex-wrap">
                                        <button className="btn btn-sm btn-outline-secondary flex-grow-1" onClick={() => applyPreset("3x11")}>3x11</button>
                                        <button className="btn btn-sm btn-outline-secondary flex-grow-1" onClick={() => applyPreset("3x8")}>3x8</button>
                                        <button className="btn btn-sm btn-outline-secondary flex-grow-1" onClick={() => applyPreset("4x11")}>4x11</button>
                                    </div>
                                </div>

                                <hr />

                                {/* Dimensions */}
                                <div className="row g-2 mb-3">
                                    <div className="col-12 mb-2">
                                        <div className="form-check form-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="autoFitSwitch"
                                                checked={settings.autoFit}
                                                onChange={e => setSettings({ ...settings, autoFit: e.target.checked })}
                                            />
                                            <label className="form-check-label small fw-bold" htmlFor="autoFitSwitch">
                                                คำนวณขนาดอัตโนมัติ (เต็มหน้า A4)
                                            </label>
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label small">คอลัมน์</label>
                                        <input type="number" className="form-control form-control-sm" value={settings.cols} onChange={e => setSettings({ ...settings, cols: Number(e.target.value) })} />
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label small">แถว</label>
                                        <input type="number" className="form-control form-control-sm" value={settings.rows} onChange={e => setSettings({ ...settings, rows: Number(e.target.value) })} />
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label small">กว้าง (mm)</label>
                                        <input
                                            type="number"
                                            className="form-control form-control-sm"
                                            value={settings.labelWidth}
                                            onChange={e => setSettings({ ...settings, labelWidth: Number(e.target.value) })}
                                            disabled={settings.autoFit}
                                        />
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label small">สูง (mm)</label>
                                        <input
                                            type="number"
                                            className="form-control form-control-sm"
                                            value={settings.labelHeight}
                                            onChange={e => setSettings({ ...settings, labelHeight: Number(e.target.value) })}
                                            disabled={settings.autoFit}
                                        />
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label small">Gap X</label>
                                        <input type="number" className="form-control form-control-sm" value={settings.gapX} onChange={e => setSettings({ ...settings, gapX: Number(e.target.value) })} />
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label small">Gap Y</label>
                                        <input type="number" className="form-control form-control-sm" value={settings.gapY} onChange={e => setSettings({ ...settings, gapY: Number(e.target.value) })} />
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label small">เว้นขอบ (mm)</label>
                                        <input type="number" className="form-control form-control-sm" value={settings.padding} onChange={e => setSettings({ ...settings, padding: Number(e.target.value) })} />
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label small">ขอบกระดาษ (mm)</label>
                                        <input type="number" className="form-control form-control-sm" value={settings.pageMargin} onChange={e => setSettings({ ...settings, pageMargin: Number(e.target.value) })} />
                                    </div>
                                </div>

                                <hr />

                                {/* Size Settings */}
                                <div className="mb-3">
                                    <label className="form-label small fw-bold">
                                        {settings.barcodeType === "2D" ? "ขนาด QR Code" : "ขนาด Barcode"}
                                    </label>
                                    <div className="row g-2">
                                        <div className="col-12">
                                            <label className="form-label small text-muted">
                                                {settings.barcodeType === "2D" ? "ขนาด (px)" : "ความสูง (px)"}
                                            </label>
                                            <input
                                                type="range"
                                                className="form-range"
                                                min="20"
                                                max="150"
                                                value={settings.barcodeHeight}
                                                onChange={e => setSettings({ ...settings, barcodeHeight: Number(e.target.value) })}
                                            />
                                        </div>
                                        {settings.barcodeType === "1D" && (
                                            <div className="col-12">
                                                <label className="form-label small text-muted">ความกว้างแท่ง (Scale)</label>
                                                <input
                                                    type="range"
                                                    className="form-range"
                                                    min="1"
                                                    max="4"
                                                    step="0.1"
                                                    value={settings.barcodeWidth}
                                                    onChange={e => setSettings({ ...settings, barcodeWidth: Number(e.target.value) })}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <hr />

                                {/* Content Settings */}
                                <div className="mb-3">
                                    <label className="form-label small fw-bold">การแสดงผล</label>
                                    <div className="form-check form-switch">
                                        <input className="form-check-input" type="checkbox" checked={settings.showName} onChange={e => setSettings({ ...settings, showName: e.target.checked })} />
                                        <label className="form-check-label small">แสดงชื่อสินค้า</label>
                                    </div>
                                    <div className="form-check form-switch">
                                        <input className="form-check-input" type="checkbox" checked={settings.showPrice} onChange={e => setSettings({ ...settings, showPrice: e.target.checked })} />
                                        <label className="form-check-label small">แสดงราคา</label>
                                    </div>
                                    <div className="form-check form-switch">
                                        <input className="form-check-input" type="checkbox" checked={settings.showCode} onChange={e => setSettings({ ...settings, showCode: e.target.checked })} />
                                        <label className="form-check-label small">แสดงรหัส</label>
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label small fw-bold">เริ่มพิมพ์ที่ดวงที่ (Offset)</label>
                                    <input type="number" className="form-control form-control-sm" min="0" value={settings.startOffset} onChange={e => setSettings({ ...settings, startOffset: Number(e.target.value) })} />
                                </div>

                                <button className="btn btn-ks-primary w-100" onClick={handlePrint}>
                                    <i className="bi bi-printer me-2"></i>สั่งพิมพ์
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <style>{`
        @media print {
          .no-print { display: none !important; }
          .barcode-page { background: white !important; }
          .card { box-shadow: none !important; border: none !important; }
          /* Remove dashed border on print if desired, or keep for cutting guides */
          .barcode-label { border: none !important; } 
          
          /* Reset scale for printing */
          .print-preview-content {
            transform: none !important;
            width: 210mm !important;
            height: 297mm !important;
            box-shadow: none !important;
            margin: 0 !important;
          }
        }
        
        /* Gold Outline Button */
        .btn-outline-gold {
            color: #d4af37;
            border-color: #d4af37;
        }
        .btn-outline-gold:hover {
            background-color: #d4af37;
            color: white;
        }
        .btn-check:checked + .btn-outline-gold {
            background-color: #d4af37;
            color: white;
            border-color: #d4af37;
        }
      `}</style>
        </div>
    );
};

export default BarcodePage;
